export const COURSE_CATALOG_PAGE_SIZE = 6;

const normalizeText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const getCourseDurationMinutes = (course = {}) =>
  (course?.modules || []).reduce(
    (moduleTotal, module) =>
      moduleTotal +
      (module?.lessons || []).reduce(
        (lessonTotal, lesson) =>
          lessonTotal + Math.max(0, Number(lesson?.durationMinutes || 0)),
        0,
      ),
    0,
  );

export const formatCourseDuration = (durationMinutes) => {
  const minutes = Math.max(0, Math.round(Number(durationMinutes || 0)));
  if (!minutes) return 'Duração não informada';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}min` : `${hours}h`;
};

export const formatCourseStatus = (status = '') => {
  const normalized = normalizeText(status).replace(/[-\s]+/g, '_');
  const labels = {
    published: 'Publicado',
    publicado: 'Publicado',
    draft: 'Rascunho',
    rascunho: 'Rascunho',
    archived: 'Arquivado',
    arquivado: 'Arquivado',
    active: 'Ativo',
    ativo: 'Ativo',
    inactive: 'Inativo',
    inativo: 'Inativo',
  };
  return labels[normalized] || String(status || 'Não informado');
};

export const formatCourseDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) return raw;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

export const filterAndSortCourses = (
  courses = [],
  {
    query = '',
    enrollmentFilter = 'all',
    enrolledCourseIds = [],
    sort = 'title',
  } = {},
) => {
  const normalizedQuery = normalizeText(query);
  const enrolledIds = new Set((enrolledCourseIds || []).map((id) => String(id)));

  const filtered = (Array.isArray(courses) ? courses : []).filter((course) => {
    const enrolled = enrolledIds.has(String(course?.id));
    if (enrollmentFilter === 'enrolled' && !enrolled) return false;
    if (enrollmentFilter === 'not-enrolled' && enrolled) return false;
    if (!normalizedQuery) return true;

    const searchable = [
      course?.title,
      course?.description,
      course?.difficulty,
      formatCourseStatus(course?.status),
      course?.creatorName,
    ]
      .map(normalizeText)
      .join(' ');

    return searchable.includes(normalizedQuery);
  });

  return [...filtered].sort((left, right) => {
    if (sort === 'duration-asc' || sort === 'duration-desc') {
      const direction = sort === 'duration-asc' ? 1 : -1;
      return (
        (getCourseDurationMinutes(left) - getCourseDurationMinutes(right)) *
        direction
      );
    }
    if (sort === 'newest') {
      return Number(right?.id || 0) - Number(left?.id || 0);
    }
    return String(left?.title || '').localeCompare(String(right?.title || ''), 'pt-BR', {
      sensitivity: 'base',
    });
  });
};

export const paginateCourseCatalog = (
  courses = [],
  requestedPage = 1,
  pageSize = COURSE_CATALOG_PAGE_SIZE,
) => {
  const safeItems = Array.isArray(courses) ? courses : [];
  const safePageSize = Math.max(1, Number(pageSize || COURSE_CATALOG_PAGE_SIZE));
  const totalItems = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const page = Math.min(totalPages, Math.max(1, Number(requestedPage || 1)));
  const startIndex = (page - 1) * safePageSize;
  const items = safeItems.slice(startIndex, startIndex + safePageSize);

  return {
    items,
    page,
    pageSize: safePageSize,
    totalItems,
    totalPages,
    from: totalItems ? startIndex + 1 : 0,
    to: Math.min(startIndex + safePageSize, totalItems),
  };
};

export const normalizeAttendanceStatus = (status = '') => {
  const normalized = normalizeText(status).replace(/[-\s]+/g, '_');
  if (['presente', 'present'].includes(normalized)) return 'PRESENTE';
  if (['falta', 'ausente', 'absent'].includes(normalized)) return 'FALTA';
  if (['justificado', 'justificada', 'justified'].includes(normalized)) return 'JUSTIFICADO';
  return String(status || '').toUpperCase();
};

export const formatAttendanceStatus = (status = '') => {
  const normalized = normalizeAttendanceStatus(status);
  if (normalized === 'PRESENTE') return 'Presente';
  if (normalized === 'FALTA') return 'Ausente';
  if (normalized === 'JUSTIFICADO') return 'Justificado';
  return status || 'Não informado';
};

export const countAttendanceStatuses = (records = []) =>
  (Array.isArray(records) ? records : []).reduce(
    (totals, record) => {
      const status = normalizeAttendanceStatus(record?.status);
      if (status === 'PRESENTE') totals.present += 1;
      if (status === 'FALTA') totals.absent += 1;
      if (status === 'JUSTIFICADO') totals.justified += 1;
      return totals;
    },
    { present: 0, absent: 0, justified: 0 },
  );
